import { useEffect, useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { GOOGLE_REVIEWS_URL } from "../content/siteContent";
import { IconGoogle, IconStar, IconArrow } from "./Icons";

// Live Google reviews via the clinic's Featurable widget API (free plan,
// auto-syncs ~daily). Provider-isolated: to replace the provider later,
// swap only this file — the section design stays unchanged.
const DEFAULT_WIDGET_ID = "400cf992-4d9a-4d00-9162-729b67d3ea9b";
const WIDGET_ID = process.env.REACT_APP_GOOGLE_REVIEWS_WIDGET_ID || DEFAULT_WIDGET_ID;
const API_URL = `https://api.featurable.com/v2/widgets/${WIDGET_ID}`;

const usePerView = () => {
    const get = () => (window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1);
    const [perView, setPerView] = useState(get);
    useEffect(() => {
        const onResize = () => setPerView(get());
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    return perView;
};

const relDate = (iso, isRtl) => {
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (days < 1) return isRtl ? "اليوم" : "Today";
    if (days < 7) return isRtl ? `منذ ${days} ${days === 1 ? "يوم" : days === 2 ? "يومين" : "أيام"}` : `${days} day${days > 1 ? "s" : ""} ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 5) return isRtl ? `منذ ${weeks} ${weeks === 1 ? "أسبوع" : weeks === 2 ? "أسبوعين" : "أسابيع"}` : `${weeks} week${weeks > 1 ? "s" : ""} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return isRtl ? `منذ ${months} ${months === 1 ? "شهر" : months === 2 ? "شهرين" : "أشهر"}` : `${months} month${months > 1 ? "s" : ""} ago`;
    const years = Math.floor(days / 365);
    return isRtl ? `منذ ${years} ${years === 1 ? "سنة" : "سنوات"}` : `${years} year${years > 1 ? "s" : ""} ago`;
};

const ReviewCard = ({ review, index, total, t, lang, isRtl }) => {
    const [expanded, setExpanded] = useState(false);
    const [avatarOk, setAvatarOk] = useState(true);
    const text = (lang === "ar" ? review.originalText || review.text : review.text || review.originalText) || "";
    const name = review.author?.name || "Google";
    const rating = review.rating?.value || 5;
    const long = text.length > 180;

    return (
        <figure
            className="flex h-full flex-col rounded-2xl border border-ink/10 bg-surface p-6 shadow-sm"
            role="group"
            aria-roledescription="slide"
            aria-label={`${index + 1} / ${total}`}
            data-testid={`review-card-${index + 1}`}
        >
            <div className="flex items-center gap-3">
                {avatarOk && review.author?.avatarUrl ? (
                    <img
                        src={review.author.avatarUrl}
                        alt=""
                        width="40"
                        height="40"
                        loading="lazy"
                        referrerPolicy="no-referrer"
                        onError={() => setAvatarOk(false)}
                        className="h-10 w-10 rounded-full border border-ink/10 object-cover"
                    />
                ) : (
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gold-100 text-sm font-bold text-pine" aria-hidden="true">
                        {name.trim().charAt(0)}
                    </span>
                )}
                <div className="min-w-0">
                    <figcaption className="truncate text-sm font-bold text-ink">{name}</figcaption>
                    <span className="text-xs text-mist">{relDate(review.publishedAt, isRtl)}</span>
                </div>
                <IconGoogle className="ms-auto h-4 w-4 shrink-0 text-mist/60" />
            </div>
            <span className="mt-3 flex gap-0.5 text-gold-500" aria-label={`${rating}/5`}>
                {Array.from({ length: rating }).map((_, i) => (
                    <IconStar key={i} className="h-3.5 w-3.5" />
                ))}
            </span>
            <blockquote dir="auto" className={`mt-3 text-sm leading-relaxed text-mist ${expanded ? "" : "line-clamp-4"}`}>
                {text}
            </blockquote>
            {long && (
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    data-testid={`review-readmore-${index + 1}`}
                    className="mt-2 self-start text-xs font-bold text-pine underline decoration-gold-500 underline-offset-4 transition-colors duration-300 hover:text-gold-600"
                >
                    {expanded ? t.reviews.readLess : t.reviews.readMore}
                </button>
            )}
        </figure>
    );
};

const GoogleReviewsWidget = () => {
    const { t, lang, isRtl } = useLanguage();
    const hostRef = useRef(null);
    const touchX = useRef(null);
    const [state, setState] = useState(WIDGET_ID ? "idle" : "fallback");
    const [reviews, setReviews] = useState([]);
    const [page, setPage] = useState(0);
    const [paused, setPaused] = useState(false);
    const perView = usePerView();
    const reduce = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    useEffect(() => {
        if (state !== "idle") return;
        const host = hostRef.current;
        if (!host) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                observer.disconnect();
                setState("loading");
                const controller = new AbortController();
                const timeout = setTimeout(() => controller.abort(), 12000);
                fetch(API_URL, { signal: controller.signal })
                    .then((r) => r.json())
                    .then((d) => {
                        clearTimeout(timeout);
                        const list = d?.widget?.reviews;
                        if (!d?.success || !Array.isArray(list) || list.length === 0) throw new Error("empty");
                        setReviews(list);
                        setState("ready");
                    })
                    .catch(() => {
                        clearTimeout(timeout);
                        setState("fallback");
                    });
            },
            { rootMargin: "300px" },
        );
        observer.observe(host);
        return () => observer.disconnect();
    }, [state]);

    const pages = Math.max(1, Math.ceil(reviews.length / perView));
    const clampedPage = Math.min(page, pages - 1);

    useEffect(() => {
        if (state !== "ready" || paused || reduce || pages < 2) return;
        const timer = setInterval(() => setPage((p) => (p + 1) % pages), 5500);
        return () => clearInterval(timer);
    }, [state, paused, reduce, pages]);

    const go = (dir) => setPage((p) => (p + dir + pages) % pages);

    const onTouchStart = (e) => {
        touchX.current = e.touches[0].clientX;
        setPaused(true);
    };
    const onTouchEnd = (e) => {
        if (touchX.current === null) return;
        const delta = e.changedTouches[0].clientX - touchX.current;
        if (Math.abs(delta) > 40) go(delta < 0 ? 1 : -1);
        touchX.current = null;
        setPaused(false);
    };

    return (
        <div ref={hostRef} className="min-h-[240px]" data-testid="google-reviews-widget">
            {(state === "idle" || state === "loading") && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" role="status" aria-label={t.reviews.loading}>
                    {[0, 1, 2].map((i) => (
                        <div
                            key={i}
                            className={`h-56 animate-pulse rounded-2xl border border-ink/10 bg-surface ${
                                i === 2 ? "hidden lg:block" : i === 1 ? "hidden sm:block" : ""
                            }`}
                        />
                    ))}
                </div>
            )}

            {state === "ready" && (
                <div
                    role="region"
                    aria-roledescription="carousel"
                    aria-label={t.reviews.title}
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onFocus={() => setPaused(true)}
                    onBlur={() => setPaused(false)}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    <div className="overflow-hidden">
                        <div
                            dir="ltr"
                            className="flex transition-transform duration-500 ease-out motion-reduce:transition-none"
                            style={{ transform: `translateX(-${clampedPage * 100}%)` }}
                            data-testid="reviews-track"
                        >
                            {Array.from({ length: pages }).map((_, p) => (
                                <div key={p} className="grid w-full shrink-0 auto-rows-fr gap-4 px-0.5" style={{ gridTemplateColumns: `repeat(${perView}, minmax(0, 1fr))` }}>
                                    {reviews.slice(p * perView, p * perView + perView).map((review, i) => (
                                        <div dir={isRtl ? "rtl" : "ltr"} key={review.id} className="h-full">
                                            <ReviewCard review={review} index={p * perView + i} total={reviews.length} t={t} lang={lang} isRtl={isRtl} />
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="mt-7 flex items-center justify-center gap-4">
                        <button
                            type="button"
                            onClick={() => go(-1)}
                            aria-label={t.reviews.prev}
                            data-testid="reviews-prev"
                            className="flex min-h-11 min-w-11 items-center justify-center rounded-full border border-ink/15 text-pine transition-colors duration-300 hover:border-gold-500 hover:text-gold-600"
                        >
                            <IconArrow className="h-4 w-4" direction="backward" />
                        </button>
                        <div className="flex gap-2" role="tablist" aria-label={t.reviews.title}>
                            {Array.from({ length: pages }).map((_, p) => (
                                <button
                                    key={p}
                                    type="button"
                                    role="tab"
                                    aria-selected={p === clampedPage}
                                    aria-label={`${p + 1} / ${pages}`}
                                    data-testid={`reviews-dot-${p + 1}`}
                                    onClick={() => setPage(p)}
                                    className={`h-2.5 rounded-full transition-[width,background-color] duration-300 ${
                                        p === clampedPage ? "w-7 bg-gold-500" : "w-2.5 bg-ink/15 hover:bg-gold-500/50"
                                    }`}
                                />
                            ))}
                        </div>
                        <button
                            type="button"
                            onClick={() => go(1)}
                            aria-label={t.reviews.next}
                            data-testid="reviews-next"
                            className="flex min-h-11 min-w-11 items-center justify-center rounded-full border border-ink/15 text-pine transition-colors duration-300 hover:border-gold-500 hover:text-gold-600"
                        >
                            <IconArrow className="h-4 w-4" direction="forward" />
                        </button>
                    </div>
                </div>
            )}

            {state === "fallback" && (
                <div className="rounded-2xl border border-ink/10 bg-surface p-8 text-center md:p-10" data-testid="reviews-fallback">
                    <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold-100 text-pine">
                        <IconGoogle className="h-6 w-6" />
                    </span>
                    <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-mist md:text-base">{t.reviews.unavailable}</p>
                    <a
                        href={GOOGLE_REVIEWS_URL}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="reviews-fallback-link"
                        className="mt-6 inline-flex min-h-[48px] items-center gap-2.5 rounded-full bg-gold-500 px-7 text-sm font-semibold text-ink transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold-600 active:scale-95"
                    >
                        {t.reviews.viewOnGoogle}
                        <IconArrow className="h-4 w-4" />
                    </a>
                </div>
            )}
        </div>
    );
};

export default GoogleReviewsWidget;
